import { useState } from 'react';
import type { Standing } from './roster';

interface Props {
  players: readonly Standing[];
  activeParticipantId?: string | null;
  /** Highlights the viewer on the player view. */
  meId?: string | null;
  /** Host-only controls. Omit for the TV view. */
  onAdjust?: (participantId: string, delta: number) => void;
  onRemove?: (participantId: string) => void;
  /**
   * Fold one row into another when the same person shows up twice. Two taps:
   * ⇄ arms the duplicate, then the row they really are absorbs it.
   */
  onMerge?: (fromId: string, intoId: string) => void;
  /** Show a live/offline dot beside each name — the TV is the only screen with
   *  room for it, and the room can't otherwise tell who dropped off. */
  showPresence?: boolean;
}

/** Highest score first, ties broken by name so the order doesn't jitter. */
function byScore(a: Standing, b: Standing): number {
  return b.score - a.score || a.name.localeCompare(b.name);
}

/** Host controls are props, not calls made here: the kit renders, the shell holds credentials. */
export function Scoreboard({
  players,
  activeParticipantId,
  meId,
  onAdjust,
  onRemove,
  onMerge,
  showPresence,
}: Props) {
  // The duplicate waiting to be folded away, if the host has armed one.
  // Resolved against the live list every render, so a row that gets merged or
  // removed out from under it simply disarms itself.
  const [mergeFrom, setMergeFrom] = useState<string | null>(null);
  const source = players.find((p) => p.id === mergeFrom) ?? null;

  if (players.length === 0) {
    return <p className="faint">Nobody has joined yet.</p>;
  }

  function absorb(target: Standing) {
    if (!source || !onMerge || target.id === source.id) return;
    if (
      confirm(
        `Fold “${source.name}” (${source.score}) into “${target.name}” (${target.score})?\n\n` +
          `${target.name} keeps ${source.score + target.score} and every buzz, answer and wager ` +
          `from both. “${source.name}” disappears.`,
      )
    ) {
      onMerge(source.id, target.id);
    }
    setMergeFrom(null);
  }

  return (
    <ol className="leaderboard">
      {source ? (
        <li className="lb-merge-bar">
          <span>
            Folding <strong>{source.name}</strong> into… pick the row they really are.
          </span>
          <span className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={() => setMergeFrom(null)}>
            Cancel
          </button>
        </li>
      ) : null}

      {[...players].sort(byScore).map((player, index) => (
        <li
          key={player.id}
          className={[
            player.id === activeParticipantId ? 'is-active' : '',
            player.connected ? '' : 'is-offline',
            source && player.id !== source.id ? 'is-merge-target' : '',
            source?.id === player.id ? 'is-merge-source' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={source && player.id !== source.id ? () => absorb(player) : undefined}
        >
          <span className="lb-rank">{index + 1}</span>
          {showPresence ? (
            <span className="lb-name has-presence">
              <span
                className={`lb-dot${player.connected ? ' is-on' : ''}`}
                title={player.connected ? 'Connected' : 'Disconnected'}
                aria-label={player.connected ? 'Connected' : 'Disconnected'}
                role="img"
              />
              <span className="lb-name-text">
                {player.name}
                {player.id === meId ? ' (you)' : ''}
              </span>
            </span>
          ) : (
            <span className="lb-name">
              {player.name}
              {player.id === meId ? ' (you)' : ''}
              {player.connected ? '' : ' ·'}
            </span>
          )}

          {onAdjust || onRemove || onMerge ? (
            <span className="row" style={{ gap: '0.35rem' }}>
              <span className={`lb-score${player.score < 0 ? ' is-negative' : ''}`}>
                {player.score}
              </span>
              {/* While a merge is armed the whole row is one big target, so the
                  per-row buttons stand down rather than swallow the click. */}
              {source ? (
                <span className="lb-admin">
                  <span className="faint">
                    {player.id === source.id ? 'folding…' : 'merge into'}
                  </span>
                </span>
              ) : (
                <span className="lb-admin">
                  {onAdjust ? (
                    <>
                      <button
                        className="btn btn-sm btn-ghost"
                        title="Subtract 100"
                        onClick={() => onAdjust(player.id, -100)}
                      >
                        −
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        title="Add 100"
                        onClick={() => onAdjust(player.id, 100)}
                      >
                        +
                      </button>
                    </>
                  ) : null}
                  {/* Only worth offering once there's something to merge with. */}
                  {onMerge && players.length > 1 ? (
                    <button
                      className="btn btn-sm btn-ghost"
                      title={`${player.name} is a duplicate — fold them into another player`}
                      onClick={() => setMergeFrom(player.id)}
                    >
                      ⇄
                    </button>
                  ) : null}
                  {onRemove ? (
                    <button
                      className="btn btn-sm btn-ghost"
                      title={`Remove ${player.name}`}
                      onClick={() => {
                        if (confirm(`Remove ${player.name} from the game?`)) onRemove(player.id);
                      }}
                    >
                      ×
                    </button>
                  ) : null}
                </span>
              )}
            </span>
          ) : (
            <span className={`lb-score${player.score < 0 ? ' is-negative' : ''}`}>
              {player.score}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}
